/**
 * Keeps the app pinned in place when the iOS software keyboard opens: the
 * keyboard overlays the page, nothing is shoved upward, and no background is
 * exposed. Not installed at all outside iOS; no-op on iOS while the keyboard
 * is closed.
 *
 * WHAT iOS ACTUALLY DOES (all measured on-device against this app; iOS Chrome
 * is WebKit too, so Safari and Chrome behave identically)
 * ---------------------------------------------------------------------------
 * When the keyboard opens, WebKit keeps the LAYOUT viewport at full height,
 * shrinks only the VISUAL viewport, and then *pans* the visual viewport down
 * (visualViewport.offsetTop, mirrored by window.scrollY on iOS) to reveal the
 * focused input. The pan range is document height minus visual height, so any
 * page as tall as the layout viewport can be shoved up by the keyboard height.
 *
 * Three progressively stronger attempts were measured before this design:
 *  1. `overflow: hidden` on html/body — no effect: it blocks only user
 *     scrolling; WebKit's reveal-the-input scroll is programmatic.
 *  2. `position: fixed` body — removed the document's *content* overflow
 *     (verified "overflow 0 locked") but the pan continued: html still spans
 *     the layout viewport, which alone gives WebKit its pan range.
 *  3. Sizing/translating body to *follow* the pan — content stopped shifting,
 *     but the render surface still slid up by the keyboard height (measured
 *     vvTop peak 334) and the canvas showed through while the follow lagged.
 *
 * THE DESIGN
 * ----------
 * While the keyboard is open (visual viewport meaningfully shorter than its
 * recent maximum, or panned):
 *   - Size BOTH `html` and `body` to the visual viewport height. html matters:
 *     documentElement's box is what keeps the document tall enough to give
 *     WebKit somewhere to pan; collapsing it collapses the pan/scroll range.
 *   - Stamp `data-ios-keyboard` on the root element. index.css uses it to make
 *     #root scrollable while engaged: pages sized with `100vh` (which tracks
 *     the layout viewport and ignores the keyboard) would otherwise be clipped
 *     by the pinned body with the hidden strip unreachable — we suppressed the
 *     WebKit pan that used to reveal it. A scrollable #root turns it into one
 *     more inner scroller, which both the user and the reveal below can move.
 *   - Reset any pan WebKit already performed with `window.scrollTo(0, 0)`,
 *     re-applied on every visualViewport scroll tick until it converges.
 *   - Keep a `translateY` on body equal to any residual offset so the app
 *     covers the visible strip even mid-animation. The transform also makes
 *     body the containing block for `position: fixed` descendants, so the
 *     fullScreen CreateReservationDialog (portalled to document.body, outside
 *     #root) inherits the corrected geometry.
 *   - Re-reveal the focused field with scrollIntoView — on first engage and on
 *     every later focus change while engaged (keyboard Next/prev arrows move
 *     focus without any visualViewport event). The window's scroll range is
 *     zero at that point, so only the INNER scrollers move (the dialog paper /
 *     layout containers) — which is the UX we want: the field scrolls into
 *     view inside the dialog, the frame stays put.
 *
 * Sizing the app to the visible strip is what makes the inner-scroller reveal
 * possible at all: the dialog is `inset: 0` against body, so a full-height
 * body would leave the focused field "already in view" of a full-height paper
 * and WebKit would fall back to panning the page again. The area the app
 * vacates is exactly the area under the keyboard.
 *
 * SAFETY
 * ------
 * Installed only on iOS (every iOS browser is WebKit, so a UA gate covers them
 * all; desktop and Android never get listeners). Engagement is keyed off the
 * visual viewport being shorter than its maximum since the last real geometry
 * change (i.e. a keyboard) or panned; pinch-zoom is explicitly excluded
 * (scale > 1) so we never fight a zoomed-in user's panning. The baseline
 * restarts whenever the visual viewport's WIDTH changes: the keyboard only
 * ever shrinks the height, while rotation and iPad Split View change the
 * width — so a stale maximum can't survive either and misread the shorter new
 * geometry as a permanently-open keyboard. (Neither `orientationchange` nor
 * `window` resize can drive that reset: iOS fires the former before viewport
 * metrics settle, and the latter also fires for the keyboard itself because
 * iOS innerHeight tracks the visual viewport.) When idle every touched style
 * is removed, leaving the DOM exactly as authored — including no transform on
 * body, which would otherwise re-anchor fixed elements app-wide.
 */

/** Ignore sub-pixel and URL-bar-settling noise below this many CSS px. */
const EPSILON = 2;

const isIos = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as "Macintosh"; real Macs have no touch points.
    (navigator.userAgent.includes('Macintosh') &&
      navigator.maxTouchPoints > 1));

export function installIosKeyboardViewportFix(): () => void {
  const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
  if (!vv || !isIos()) return () => {};

  let engaged = false;
  let maxVvHeight = 0;
  // Last values written to the DOM; skipping unchanged writes keeps the
  // steady state (vv scroll ticks with constant geometry) free of style
  // invalidations while the device is busy animating the keyboard.
  let lastHeight = -1;
  let lastOffset = -1;
  let lastVvWidth = -1;
  let revealTimer = 0;

  const clear = () => {
    window.clearTimeout(revealTimer);
    if (!engaged) return;
    engaged = false;
    lastHeight = -1;
    lastOffset = -1;
    document.documentElement.style.removeProperty('height');
    document.documentElement.removeAttribute('data-ios-keyboard');
    document.body.style.removeProperty('height');
    document.body.style.removeProperty('transform');
  };

  const revealFocusedField = () => {
    if (!engaged) return;
    const el = document.activeElement;
    const isEditable =
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLElement && el.isContentEditable);
    if (isEditable) {
      // Window scroll range is zero while engaged, so this moves only the
      // inner scrollers (dialog paper / layout containers), not the page.
      el.scrollIntoView({ block: 'center' });
    }
  };

  const scheduleReveal = () => {
    window.clearTimeout(revealTimer);
    revealTimer = window.setTimeout(revealFocusedField, 50);
  };

  const sync = () => {
    // Pinch zoom also shrinks the visual viewport — never fight that pan.
    if (vv.scale > 1.05) {
      clear();
      return;
    }

    // A width change is a real geometry change (rotation, iPad Split View);
    // the keyboard only ever shrinks the height. Restart the baseline so the
    // old geometry's maximum can't masquerade as a keyboard inset. This runs
    // on settled metrics, unlike `orientationchange`, which iOS fires before
    // the viewport updates.
    if (Math.abs(vv.width - lastVvWidth) > EPSILON) {
      lastVvWidth = vv.width;
      maxVvHeight = 0;
    }

    maxVvHeight = Math.max(maxVvHeight, vv.height);
    const keyboardInset = maxVvHeight - vv.height;
    const panned = vv.offsetTop;

    if (keyboardInset <= EPSILON && panned <= EPSILON) {
      clear();
      return;
    }

    const firstEngage = !engaged;
    engaged = true;

    const height = Math.round(vv.height);
    if (height !== lastHeight) {
      lastHeight = height;
      document.documentElement.style.height = `${height}px`;
      document.body.style.height = `${height}px`;
    }

    // Undo the pan rather than following it. Runs again on every
    // visualViewport scroll tick, so it converges to 0 during the keyboard
    // animation and then stops firing (scrollTo at 0 emits no event).
    if (window.scrollY > 0 || panned > 0) {
      window.scrollTo(0, 0);
    }

    // Cover any offset that remains this tick so no canvas shows mid-animation.
    const offset = Math.round(vv.offsetTop);
    if (offset !== lastOffset) {
      lastOffset = offset;
      document.body.style.transform = `translateY(${offset}px)`;
    }

    if (firstEngage) {
      // Activates the scoped rules in index.css (scrollable #root, bounded
      // dialog papers) for as long as the fix is engaged.
      document.documentElement.setAttribute('data-ios-keyboard', '');
      // We cancelled WebKit's own reveal; do it through the inner scrollers
      // once the geometry above has been applied.
      scheduleReveal();
    }
  };

  // Moving focus between fields with the keyboard already open fires no
  // visualViewport event, and the window-level reveal we suppressed is what
  // WebKit would have used — so re-reveal ourselves.
  const onFocusIn = () => {
    if (engaged) scheduleReveal();
  };

  vv.addEventListener('resize', sync);
  vv.addEventListener('scroll', sync);
  document.addEventListener('focusin', onFocusIn);
  sync();

  return () => {
    vv.removeEventListener('resize', sync);
    vv.removeEventListener('scroll', sync);
    document.removeEventListener('focusin', onFocusIn);
    clear();
  };
}
